import type { HorizonCourseCell, HorizonOutlet, HorizonSignal } from './horizonTypes';
import { metricAvailable, monthShort } from './horizonFormat';

/** A day is open only when an outlet can actually seat guests on it. */
export type HorizonDayState = 'open' | 'closed' | 'not_bookable';

export type HorizonDay = {
  serviceDate: string;
  /** ISO weekday, 1 = Monday, matching the backend `weekday` field. */
  weekday: number;
  daysAhead: number;
  state: HorizonDayState;
  /** Counted covers on the books. Null when no outlet is open. */
  onBooks: number | null;
  /** Counted cover capacity across open outlets. */
  capacity: number | null;
  occupancyPct: number | null;
  openOutlets: number;
  closedOutlets: number;
  /** Outlets whose bookable window has not reached this date yet. */
  pendingOutlets: number;
  totalOutlets: number;
  beyondTrusted: boolean;
  holiday: boolean;
  /** Covers the recent same-weekday services actually served, summed over the
   * open outlets. Null when no outlet has enough comparable services. */
  typicalCovers: number | null;
  /** How many same-weekday services back the benchmark, at its thinnest outlet. */
  benchmarkServices: number;
  /** Survived covers expected now versus a typical same-weekday at this lead. */
  paceExpectedNow: number | null;
  paceTypicalAtLead: number | null;
  /**
   * What comparable dates had booked at this same lead time, summed over the
   * outlets that have one, against this date's book at those outlets.
   */
  bookPace: {
    reference: number;
    booked: number;
    vsPct: number | null;
    basis: 'lastYear' | 'recentWeeks' | 'mixed';
  } | null;
  cells: HorizonCourseCell[];
};

export type HorizonWeek = {
  key: string;
  /** Emitted on the first week and whenever a new month opens. */
  monthLabel: string | null;
  /** Always length 7, Monday first. Null where the window has no such date. */
  days: (HorizonDay | null)[];
};

export const PRESSURE_PCT = 85;

export function isOpenCell(cell: HorizonCourseCell): boolean {
  return cell.kind === 'project' || cell.kind === 'pace_only';
}

/** Pace % vs typical-at-this-lead (same denominator as the italic typical). */
export function paceDeltaPct(expectedNow: number, typicalAtLead: number): number {
  return Math.round((expectedNow / typicalAtLead - 1) * 100);
}

function monthLabel(serviceDate: string): string {
  return monthShort(serviceDate);
}

/** Below this reference a percentage says more about noise than demand. */
const MIN_PACE_REFERENCE = 5;

/**
 * Collapse the outlet × date matrix down to one entry per date.
 *
 * Passing an outlet id reads that outlet alone; passing null rolls the fleet up.
 * Capacity only counts outlets that are open, so occupancy stays a share of
 * seats a guest could actually book rather than of the whole estate.
 */
export function rollUpDays(
  cells: HorizonCourseCell[],
  outlets: HorizonOutlet[],
  outletId: string | null,
): HorizonDay[] {
  const scoped = outletId ? cells.filter(c => c.outletId === outletId) : cells;
  const totalOutlets = outletId ? 1 : Math.max(outlets.length, 1);

  const byDate = new Map<string, HorizonCourseCell[]>();
  for (const cell of scoped) {
    const list = byDate.get(cell.serviceDate);
    if (list) list.push(cell);
    else byDate.set(cell.serviceDate, [cell]);
  }

  const days: HorizonDay[] = [];
  for (const serviceDate of [...byDate.keys()].sort()) {
    const dayCells = byDate.get(serviceDate) ?? [];
    const open = dayCells.filter(isOpenCell);
    const closed = dayCells.filter(c => c.kind === 'closed');

    let onBooks: number | null = null;
    let capacity: number | null = null;
    let typicalCovers: number | null = null;
    let paceExpectedNow: number | null = null;
    let paceTypicalAtLead: number | null = null;
    // The weakest contributing benchmark sets the confidence for the whole day:
    // a fleet total is only as trustworthy as its thinnest history.
    let benchmarkServices = 0;
    let paceRef = 0;
    let paceBooked = 0;
    const paceBases = new Set<string>();
    for (const cell of open) {
      const bp = cell.bookPace;
      if (bp && bp.reference != null && bp.basis) {
        paceRef += bp.reference;
        paceBooked += metricAvailable(cell.onBooks) ? (cell.onBooks.value as number) : 0;
        paceBases.add(bp.basis);
      }
      if (metricAvailable(cell.onBooks)) {
        onBooks = (onBooks ?? 0) + (cell.onBooks.value as number);
      }
      if (metricAvailable(cell.coverCapacity)) {
        capacity = (capacity ?? 0) + (cell.coverCapacity.value as number);
      }
      const typical = cell.typicalWeekday?.typicalCovers;
      if (typical && metricAvailable(typical)) {
        typicalCovers = (typicalCovers ?? 0) + (typical.value as number);
        const services = cell.typicalWeekday.services;
        benchmarkServices =
          benchmarkServices === 0 ? services : Math.min(benchmarkServices, services);
      }
      const pace = cell.pace;
      if (pace?.available && metricAvailable(pace.expectedKeptNow)) {
        paceExpectedNow =
          (paceExpectedNow ?? 0) + (pace.expectedKeptNow.value as number);
        paceTypicalAtLead =
          (paceTypicalAtLead ?? 0) + (pace.typicalAtThisLead.value as number);
      }
    }

    const state: HorizonDayState =
      open.length > 0 ? 'open' : closed.length > 0 ? 'closed' : 'not_bookable';

    days.push({
      serviceDate,
      weekday: dayCells[0]?.weekday ?? 1,
      daysAhead: dayCells[0]?.daysAhead ?? 0,
      state,
      onBooks,
      capacity,
      occupancyPct:
        capacity && capacity > 0 && onBooks != null
          ? (onBooks / capacity) * 100
          : null,
      openOutlets: open.length,
      closedOutlets: closed.length,
      pendingOutlets: Math.max(0, totalOutlets - dayCells.length),
      totalOutlets,
      beyondTrusted: open.length > 0 && open.every(c => c.beyondTrusted),
      holiday: dayCells.some(c => c.holiday),
      typicalCovers:
        typicalCovers != null ? Math.round(typicalCovers * 10) / 10 : null,
      benchmarkServices,
      paceExpectedNow:
        paceExpectedNow != null ? Math.round(paceExpectedNow * 10) / 10 : null,
      paceTypicalAtLead:
        paceTypicalAtLead != null ? Math.round(paceTypicalAtLead * 10) / 10 : null,
      bookPace:
        paceBases.size > 0
          ? {
              reference: Math.round(paceRef * 10) / 10,
              booked: Math.round(paceBooked * 10) / 10,
              vsPct:
                paceRef >= MIN_PACE_REFERENCE
                  ? Math.round((paceBooked / paceRef - 1) * 100)
                  : null,
              basis:
                paceBases.size > 1
                  ? 'mixed'
                  : (paceBases.values().next().value as 'lastYear' | 'recentWeeks'),
            }
          : null,
      cells: dayCells,
    });
  }
  return days;
}

/** Lay days out as Monday-first calendar weeks with month labels in the gutter. */
export function groupIntoWeeks(days: HorizonDay[]): HorizonWeek[] {
  if (days.length === 0) return [];

  const weeks: HorizonWeek[] = [];
  let current: (HorizonDay | null)[] = new Array(7).fill(null);
  let started = false;

  const flush = () => {
    if (!started) return;
    const first = current.find(Boolean) as HorizonDay | undefined;
    weeks.push({
      key: first?.serviceDate ?? `w${weeks.length}`,
      monthLabel: null,
      days: current,
    });
    current = new Array(7).fill(null);
    started = false;
  };

  for (const day of days) {
    const index = Math.min(6, Math.max(0, day.weekday - 1));
    if (started && current[index] !== null) flush();
    current[index] = day;
    started = true;
    if (index === 6) flush();
  }
  flush();

  let lastLabel = '';
  for (const week of weeks) {
    const opensMonth = week.days.find(d => d && d.serviceDate.slice(8) === '01');
    const first = week.days.find(Boolean) as HorizonDay | undefined;
    const anchor = opensMonth ?? first;
    if (!anchor) continue;
    const label = monthLabel(anchor.serviceDate);
    if (!lastLabel || opensMonth) {
      week.monthLabel = label;
      lastLabel = label;
    }
  }

  return weeks;
}

/** Dates carrying a course correction, so the calendar can mark them. */
export function signalDates(
  signals: HorizonSignal[],
  outletId: string | null,
): Set<string> {
  const dates = new Set<string>();
  for (const signal of signals) {
    if (outletId && signal.outletId && signal.outletId !== outletId) continue;
    const date =
      signal.link?.serviceDate ||
      (signal.evidence.kind === 'horizonDate' ? signal.evidence.serviceDate : null);
    if (date) dates.add(date);
  }
  return dates;
}
